import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import { ReportSummaryItemApi } from "../api/Reports";
import { Account, Jobs, PersonInfoApi } from "../api/Person";
import {
  useUpdatePersonAddresses,
  useUpdatePersonDegrees,
  useUpdatePerson360Info,
  useUpdatePersonJobs,
} from "../api/PersonApi";
import { formatJob, formatDegree } from "./utils/personInfoFormat";
import { PersonConsentForm } from "../api/360Files";
import { useApplicationToast } from "../webcontent/ApplicationToastProvider";
import {
  classNames,
  copyToClipboard,
  getColorFromCheckType,
  LONG_TEXT_WRAP_CLASSES,
  uniqueUUID,
} from "../utils";
import { NameType } from "../api/Person";
import { Modal } from "../shared-components/Modal";
import CopyOutlineIcon from "../shared-components/icons/CopyOutlineIcon";
import ViewReportLogsButton from "../shared-components/ViewReportLogsButton";
import EuRegionBadge from "../shared-components/EuRegionBadge";
import Notes360 from "./Notes360";
import {
  ConfirmationDialogIcon,
  useConfirmationDialog,
} from "../shared-components/ConfirmationDialog";
import { ButtonColorTypes } from "../shared-components/Button";
import {
  sanitizeName,
  sanitizeAlias,
} from "../reports-wizard/ReportsWizardUtil";
import PersonInfoField from "./person/PersonInfoField";
import PersonEditForm from "./person/PersonEditForm";
import EditAddressModal from "./person/EditAddressModal";
import EditEmploymentModal from "./person/EditEmploymentModal";
import EditEducationModal from "./person/EditEducationModal";
import {
  formatBirthDate,
  formatAddress,
  getAge,
  getDisplayCompanyName,
  toDateInputValue,
  formStateToPersonInfoApi,
  PersonEditFormState,
} from "./person/helpers";
import PersonDocumentsSection from "./person/PersonDocumentsSection";

const REPORT_ID_TRUNCATE = 24;

type OpenEditModal = "person" | "location" | "employment" | "education" | null;

type PersonSectionKey = "addresses" | "jobs" | "degrees";

const SOURCE_360_WORKBENCH = "360 Workbench";

interface SectionSaveOptions {
  personEx: PersonInfoApi;
  personUuid: string;
  reportUuid: string;
  source?: string;
  onPersonSaved?: (p: PersonInfoApi) => void;
  onPersonSaveSuccess?: () => void;
  onPersonSaveError?: () => void;
  showPushToast?: (opts: { message: string; duration?: number }) => void;
  setOpenEditModal: (v: OpenEditModal) => void;
  setSectionUnsaved: (v: boolean) => void;
}

function createSectionSave<K extends PersonSectionKey>(
  key: K,
  mutate: (args: {
    personUuid: string;
    payload: { report_uuid: string } & Record<K, PersonInfoApi[K]>;
    source?: string;
  }) => Promise<unknown>,
  options: SectionSaveOptions
): (data: PersonInfoApi[K]) => Promise<void> {
  return async (data: PersonInfoApi[K]) => {
    try {
      const payload = {
        [key]: data,
        report_uuid: options.reportUuid,
      } as { report_uuid: string } & Record<K, PersonInfoApi[K]>;
      await mutate({
        personUuid: options.personUuid,
        payload,
        source: options.source,
      });
      const updated = { ...options.personEx, [key]: data };
      options.onPersonSaved?.(updated);
      options.onPersonSaveSuccess?.();
      options.showPushToast?.({
        message: "Person info updated.",
        duration: 2000,
      });
      options.setOpenEditModal(null);
      options.setSectionUnsaved(false);
    } catch (err) {
      options.onPersonSaveError?.();
      throw err;
    }
  };
}

interface ReportPersonInfoSidebar360Props {
  data: ReportSummaryItemApi;
  personEx: PersonInfoApi;
  accounts: Account[];
  canViewReportLogs?: boolean;
  canView360?: boolean;
  canEdit360Reports?: boolean;
  blueprintColor?: string | null;
  isReadOnly?: boolean;
  consentForms?: PersonConsentForm[];
  onPersonSaved?: (updated: PersonInfoApi) => void;
  onPersonSaveSuccess?: () => void;
  onPersonSaveError?: () => void;
}

const ReportPersonInfoSidebar360: React.FC<ReportPersonInfoSidebar360Props> = ({
  data,
  personEx,
  accounts,
  canViewReportLogs = false,
  canView360 = false,
  canEdit360Reports = false,
  blueprintColor = null,
  isReadOnly = false,
  consentForms = [],
  onPersonSaved,
  onPersonSaveSuccess,
  onPersonSaveError,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showPushToast } = useApplicationToast();
  const { mutateAsync: updatePerson360Info, isLoading: isSavingPerson } =
    useUpdatePerson360Info();
  const { mutateAsync: updatePersonAddresses, isLoading: isSavingAddresses } =
    useUpdatePersonAddresses();
  const { mutateAsync: updatePersonJobs, isLoading: isSavingJobs } =
    useUpdatePersonJobs();
  const { mutateAsync: updatePersonDegrees, isLoading: isSavingDegrees } =
    useUpdatePersonDegrees();
  const [openEditModal, setOpenEditModal] = useState<OpenEditModal>(null);
  const [sectionUnsaved, setSectionUnsaved] = useState(false);
  const {
    modal: discardModal,
    show: showDiscardModal,
    hide: hideDiscardModal,
  } = useConfirmationDialog();

  const buildInitialFormState = useCallback((): PersonEditFormState => {
    const main = personEx.names.find((n) => n.name_type === NameType.MAIN);
    const aliases = personEx.names.filter(
      (n) => n.name_type === NameType.ALIAS
    );
    return {
      primary: {
        firstName: main?.first_name ?? "",
        middleName: main?.middle_name ?? "",
        lastName: main?.last_name ?? "",
      },
      aliases:
        aliases.length > 0
          ? aliases.map((n) => ({
              key: uniqueUUID(),
              firstName: n.first_name ?? "",
              middleName: n.middle_name ?? "",
              lastName: n.last_name ?? "",
            }))
          : [],
      emails:
        personEx.emails?.length > 0
          ? personEx.emails.map((e) => ({
              key: uniqueUUID(),
              email: e.address,
            }))
          : [{ key: uniqueUUID(), email: "" }],
      phones:
        personEx.phones?.length > 0
          ? personEx.phones.map((p) => ({
              key: uniqueUUID(),
              phone: p.raw_number,
            }))
          : [{ key: uniqueUUID(), phone: "" }],
      birthDate: toDateInputValue(personEx.birth_date),
    };
  }, [personEx]);

  const [formState, setFormState] = useState<PersonEditFormState>(() => ({
    primary: { firstName: "", middleName: "", lastName: "" },
    aliases: [{ key: "", firstName: "", middleName: "", lastName: "" }],
    emails: [{ key: "", email: "" }],
    phones: [{ key: "", phone: "" }],
    birthDate: "",
  }));
  const baselineRef = useRef<PersonEditFormState | null>(null);

  useEffect(() => {
    if (openEditModal === "person") {
      const fresh = buildInitialFormState();
      setFormState(fresh);
      baselineRef.current = fresh;
    } else {
      baselineRef.current = null;
    }
  }, [openEditModal, buildInitialFormState]);

  const hasUnsavedChanges =
    baselineRef.current !== null &&
    JSON.stringify(formState) !== JSON.stringify(baselineRef.current);

  const hasUnsavedInCurrentModal =
    openEditModal === "person" ? hasUnsavedChanges : sectionUnsaved;

  const handleCloseEditModal = useCallback(() => {
    if (hasUnsavedInCurrentModal) {
      showDiscardModal(
        "Exit without Saving?",
        "Are you sure you want to exit without saving? Any unsaved changes will be lost.",
        (action) => {
          if (action === "continue") {
            setOpenEditModal(null);
            if (openEditModal !== "person") {
              setSectionUnsaved(false);
            }
            hideDiscardModal();
          } else {
            hideDiscardModal();
          }
        },
        undefined,
        true,
        "Cancel",
        ButtonColorTypes.cancel,
        "Yes, Exit",
        ButtonColorTypes.warning,
        ConfirmationDialogIcon.WARN,
        true
      );
    } else {
      setOpenEditModal(null);
    }
  }, [
    hasUnsavedInCurrentModal,
    openEditModal,
    showDiscardModal,
    hideDiscardModal,
  ]);

  const handleSavePersonEdit = useCallback(async () => {
    if (!hasUnsavedChanges) return;
    const updated = formStateToPersonInfoApi(formState, personEx);
    try {
      await updatePerson360Info({
        personUuid: data.person_uuid,
        payload: {
          birth_date: updated.birth_date ?? null,
          names: updated.names.map((n) => ({
            first_name: n.first_name,
            middle_name: n.middle_name,
            last_name: n.last_name,
            name_type: n.name_type,
          })),
          emails: updated.emails.map((e) => ({ address: e.address })),
          phones: updated.phones.map((p) => ({ raw_number: p.raw_number })),
          report_uuid: data.report_uuid,
        },
      });
      onPersonSaved?.(updated);
      onPersonSaveSuccess?.();
      showPushToast?.({
        message: "Person info updated.",
        duration: 2000,
      });
      baselineRef.current = formState;
      setOpenEditModal(null);
    } catch (err) {
      onPersonSaveError?.();
      showPushToast?.({
        message: "Failed to save person info. Please try again.",
        duration: 4000,
        type: "error",
      });
    }
  }, [
    formState,
    hasUnsavedChanges,
    data.person_uuid,
    data.report_uuid,
    onPersonSaved,
    onPersonSaveSuccess,
    onPersonSaveError,
    personEx,
    showPushToast,
    updatePerson360Info,
  ]);

  const handleCopy = () => {
    showPushToast?.({
      message: "Text copied to clipboard",
      duration: 2000,
    });
  };

  const sectionSaveOptions: SectionSaveOptions = useMemo(
    () => ({
      personEx,
      personUuid: data.person_uuid,
      reportUuid: data.report_uuid,
      source: SOURCE_360_WORKBENCH,
      onPersonSaved,
      onPersonSaveSuccess,
      onPersonSaveError,
      showPushToast,
      setOpenEditModal,
      setSectionUnsaved,
    }),
    [
      data.person_uuid,
      data.report_uuid,
      personEx,
      onPersonSaved,
      onPersonSaveSuccess,
      onPersonSaveError,
      showPushToast,
    ]
  );

  const saveAddresses = useMemo(
    () =>
      createSectionSave("addresses", updatePersonAddresses, sectionSaveOptions),
    [sectionSaveOptions, updatePersonAddresses]
  );
  const saveJobs = useMemo(
    () => createSectionSave("jobs", updatePersonJobs, sectionSaveOptions),
    [sectionSaveOptions, updatePersonJobs]
  );
  const saveDegrees = useMemo(
    () => createSectionSave("degrees", updatePersonDegrees, sectionSaveOptions),
    [sectionSaveOptions, updatePersonDegrees]
  );

  const primaryName = personEx.names.find((n) => n.name_type === NameType.MAIN);
  const displayName = primaryName
    ? [primaryName.first_name, primaryName.middle_name, primaryName.last_name]
        .filter(Boolean)
        .join(" ")
        .trim()
    : data.full_name;

  const aliasLabels = personEx.names
    .filter((n) => n.name_type === NameType.ALIAS)
    .map((n) =>
      [n.first_name, n.middle_name, n.last_name]
        .filter(Boolean)
        .join(" ")
        .trim()
    )
    .filter(Boolean);

  const dobFormatted = formatBirthDate(personEx.birth_date);
  const age = getAge(personEx.birth_date);
  const locationEntries =
    personEx.addresses && personEx.addresses.length > 0
      ? personEx.addresses.map((addr) => formatAddress(addr)).filter((s) => s)
      : undefined;

  const getEndDate = (j: Jobs & { endDate?: string }) =>
    j.end_date ?? j.endDate ?? "";
  const jobsSorted = [...(personEx.jobs || [])].sort((a, b) => {
    const endA = getEndDate(a) ? new Date(getEndDate(a)).getTime() : Infinity;
    const endB = getEndDate(b) ? new Date(getEndDate(b)).getTime() : Infinity;
    return endB - endA;
  });
  const employmentEntries =
    jobsSorted.length > 0
      ? jobsSorted.map((j) => formatJob(j)).filter(Boolean)
      : undefined;

  const getDegEndDate = (d: { end_date?: string; endDate?: string }) =>
    d.end_date ?? d.endDate ?? "";
  const degreesSorted = [...(personEx.degrees || [])].sort((a, b) => {
    const endA = getDegEndDate(a)
      ? new Date(getDegEndDate(a)).getTime()
      : Infinity;
    const endB = getDegEndDate(b)
      ? new Date(getDegEndDate(b)).getTime()
      : Infinity;
    return endB - endA;
  });
  const educationEntries =
    degreesSorted.length > 0
      ? degreesSorted.map((d) => formatDegree(d)).filter(Boolean)
      : undefined;

  const reportIdDisplay =
    data.report_uuid.length > REPORT_ID_TRUNCATE
      ? `${data.report_uuid.slice(0, REPORT_ID_TRUNCATE)}...`
      : data.report_uuid;

  const checkTypeColorClass = getColorFromCheckType(blueprintColor);
  const displayCompanyName = getDisplayCompanyName(data);

  return (
    <>
      <div className="col-span-2 print:hidden">
        <div className="flex flex-col items-stretch min-h-full border border-gray-200 rounded-lg bg-white">
          <div className="flex flex-col">
            <div className="p-4 pb-3">
              <div className="flex items-start gap-1.5 mb-1 min-w-0">
                <h2
                  className={classNames(
                    "text-xl font-bold text-gray-900 flex-1 min-w-0",
                    LONG_TEXT_WRAP_CLASSES
                  )}
                >
                  {displayName}
                  <EuRegionBadge
                    personUuid={data.person_uuid}
                    className="ml-2"
                  />
                </h2>
                {canEdit360Reports && (
                  <button
                    type="button"
                    onClick={() => setOpenEditModal("person")}
                    className="p-0.5 -m-0.5 mt-0.5 cursor-pointer text-gray-700 hover:text-gray-400 flex-shrink-0"
                    title="Edit person"
                    aria-label="Edit person"
                  >
                    <PencilSquareIcon className="w-4 h-4" aria-hidden="true" />
                  </button>
                )}
              </div>
              <p className="text-sm text-gray-600 mb-0.5">
                {displayCompanyName && (
                  <>
                    Company:{" "}
                    <span className="font-semibold text-gray-700">
                      {displayCompanyName}
                    </span>
                  </>
                )}
              </p>
              <p className="text-sm text-gray-600 mb-1">
                Checktype:{" "}
                <span className={`font-semibold ${checkTypeColorClass}`}>
                  {data.blueprint_name}
                </span>
              </p>
              <div className="flex justify-between items-center gap-2 min-w-0">
                <span className="text-sm text-gray-600 truncate whitespace-nowrap min-w-0">
                  Report ID:{" "}
                  <span className="font-semibold text-gray-700">
                    {reportIdDisplay}
                  </span>
                </span>
                <button
                  type="button"
                  className="p-0.5 cursor-pointer text-gray-500 hover:text-gray-700 flex-shrink-0"
                  onClick={async () => {
                    await copyToClipboard(data.report_uuid);
                    handleCopy();
                  }}
                  title="Copy Report ID"
                >
                  <CopyOutlineIcon className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="border-t border-gray-200 mx-4" />

            {canViewReportLogs && (
              <div className="p-4">
                <ViewReportLogsButton
                  onClick={() =>
                    navigate(`/reports/${data.report_uuid}/logs`, {
                      state: {
                        fromPath: `${location.pathname}${location.search}`,
                      },
                    })
                  }
                  canAddNotes={canViewReportLogs}
                />
              </div>
            )}

            <div className="px-4 pb-4">
              <PersonInfoField
                label="Aliases"
                values={aliasLabels.length > 0 ? aliasLabels : undefined}
                emptyPlaceholder="None"
                onCopy={handleCopy}
                showEditIcon={canEdit360Reports}
                onEditClick={() => setOpenEditModal("person")}
              />
              <PersonInfoField
                label="Email"
                values={
                  personEx.emails?.length
                    ? personEx.emails.map((e) => e.address)
                    : undefined
                }
                emptyPlaceholder="Not Provided"
                onCopy={handleCopy}
                showEditIcon={canEdit360Reports}
                onEditClick={() => setOpenEditModal("person")}
              />
              <PersonInfoField
                label="Date of Birth"
                value={dobFormatted}
                emptyPlaceholder="Not Provided"
                onCopy={handleCopy}
                showEditIcon={canEdit360Reports}
                onEditClick={() => setOpenEditModal("person")}
              />
              <PersonInfoField
                label="Age"
                value={age !== undefined ? String(age) : undefined}
                emptyPlaceholder="Not Provided"
                onCopy={handleCopy}
                showEditIcon={canEdit360Reports}
                onEditClick={() => setOpenEditModal("person")}
              />
              <PersonInfoField
                label="Phone"
                values={
                  personEx.phones?.length
                    ? personEx.phones.map((p) => p.raw_number)
                    : undefined
                }
                emptyPlaceholder="Not Provided"
                onCopy={handleCopy}
                showEditIcon={canEdit360Reports}
                onEditClick={() => setOpenEditModal("person")}
              />
              <PersonInfoField
                label="Location"
                values={locationEntries}
                emptyPlaceholder="Not Provided"
                onCopy={handleCopy}
                showEditIcon={canEdit360Reports}
                onEditClick={() => setOpenEditModal("location")}
              />
              <PersonInfoField
                label="Employment"
                values={employmentEntries}
                allowMultiline
                emptyPlaceholder="Not Provided"
                onCopy={handleCopy}
                showEditIcon={canEdit360Reports}
                onEditClick={() => setOpenEditModal("employment")}
              />
              <PersonInfoField
                label="Education"
                values={educationEntries}
                allowMultiline
                emptyPlaceholder="Not Provided"
                onCopy={handleCopy}
                showEditIcon={canEdit360Reports}
                onEditClick={() => setOpenEditModal("education")}
              />
              {canView360 && (
                <>
                  {/* Person accounts, not Items: 360 needs a static identity list
                      (including OTHER/unknown sites) that is unaffected by item
                      in_report / soft-delete / amendment. See useGetPersonAccounts. */}
                  <PersonInfoField
                    label="Social Profiles"
                    values={
                      accounts?.length ? accounts.map((a) => a.url) : undefined
                    }
                    isUrl
                    onCopy={handleCopy}
                  />

                  <PersonDocumentsSection
                    personUuid={data.person_uuid}
                    consentForms={consentForms}
                  />
                </>
              )}
            </div>
            {canViewReportLogs && (
              <div className="border-t border-gray-200">
                <Notes360 report={data} isReadOnly={isReadOnly} />
              </div>
            )}
          </div>
        </div>
      </div>

      {openEditModal === "location" && (
        <EditAddressModal
          open
          onClose={handleCloseEditModal}
          initialAddresses={personEx.addresses ?? []}
          onSave={saveAddresses}
          onDirtyChange={setSectionUnsaved}
          isSaving={isSavingAddresses}
        />
      )}
      {openEditModal === "employment" && (
        <EditEmploymentModal
          open
          onClose={handleCloseEditModal}
          initialJobs={personEx.jobs ?? []}
          onSave={saveJobs}
          onDirtyChange={setSectionUnsaved}
          isSaving={isSavingJobs}
        />
      )}
      {openEditModal === "education" && (
        <EditEducationModal
          open
          onClose={handleCloseEditModal}
          initialDegrees={personEx.degrees ?? []}
          onSave={saveDegrees}
          onDirtyChange={setSectionUnsaved}
          isSaving={isSavingDegrees}
        />
      )}

      <Modal
        open={openEditModal === "person"}
        onClose={handleCloseEditModal}
        title="Person Edit"
        width="w-full max-w-2xl"
      >
        <PersonEditForm
          formState={formState}
          setFormState={setFormState}
          onSave={handleSavePersonEdit}
          onCancel={handleCloseEditModal}
          hasUnsavedChanges={hasUnsavedChanges}
          sanitizeName={sanitizeName}
          sanitizeAlias={sanitizeAlias}
          isSaving={isSavingPerson}
        />
      </Modal>

      {discardModal}
    </>
  );
};

export default ReportPersonInfoSidebar360;
